import { StudioClickEventType } from "@CHILLNN-Inc/chillnn-abr-studio";
import { useEffect, useRef } from "react";

// eslint-disable-next-line import/no-restricted-paths
import { useLoadingScreen } from "@/components/features/side-effect/show-loading-screen-to-user/_hooks/use-loading-screen";

// 実体は外部ライブラリだが、tsconfig paths で src/types/shims の型 shim へ解決されるため
// lib 層の no-restricted-paths が「src 内部の import」と誤検知する。shim 由来の false positive のため抑制する。
// eslint-disable-next-line import/no-restricted-paths
import { ChillnnStudioEditor } from "@CHILLNN-Inc/chillnn-driver-studio-vue";

import type {
	ChillnnContentsPage,
	ChillnnImage,
	IChillnnTemplateCategoryModel,
	IChillnnTemplateModelRepository,
	ImageMast,
	OtherResource,
	Scalars,
	StudioImageMast,
} from "@CHILLNN-Inc/chillnn-abr-studio";

type Props = {
	editorProps: {
		/**
		 * DOMをJSONで表現したものであり、ChillnnStudioEditorはこのJSONデータを編集するためのエディタです。
		 * このデータはMutableです。
		 * callbackSaveが呼ばれた場合、このデータ自体をS3にアップロードしS3のエイリアスをGraphQLのAPI経由で保存してください。
		 * SnackやHPBの本体として保存されているS3Objectを取得することで、このデータを得ることができます。
		 *
		 * 編集（既に存在するスナック）の場合、S3Objectからスナックのデータを取得する必要があるので一時的に null の場合がある。
		 * そのため、コンポーネントのインスタンス化は pageが存在するようになるまで（null => ChillnnContentsPage） 待つ必要がある。
		 * （新規作成の場合は同期的処理で生成するので null にはならない）
		 */
		page: ChillnnContentsPage | null;
		/**
		 * 施設に登録されている画像を取得するための関数
		 * SnackでもHPBでも同様のAPIを使ってください
		 */
		imageRepository: () => Promise<StudioImageMast[]>;
		/**
		 * 新規に画像をアップロードするための関数
		 * Fileを受け取ってS3にアップロードを行い、GraphQLのAPI経由でS3のエイリアスを保存してください。
		 */
		imageUploader: (image: File) => Promise<ChillnnImage>;
		/**
		 * 画像を削除するための関数です。
		 * GraphQLのAPI経由で画像のエイリアスを削除してください。
		 * 画像のエイリアスは削除されますが、画像自体は削除されません。
		 */
		imageDeleter: (uuid: Scalars["ID"]) => Promise<ImageMast>; // 画像を削除するための関数
		/**
		 * ### Snack内でのボタンクリック時に発火するイベントのタイプ
		 * SnackとHPB、それぞれ下記のイベントを許可しています。
		 * - NONE：特にイベントの紐付けはしない
		 * - ExternalLink：外部リンクの紐付けを行う
		 * - StudioResource：別のスナックページ
		 * - HotelReservation：予約コンポーネントを開く
		 * - HotelReservationRoom：CHILLNNに登録されている部屋を指定して予約コンポーネントを開く
		 */
		availableClickEvents?: StudioClickEventType[]; // 利用可能なクリックイベント
		/**
		 * Snackを複数言語で設定できるようにするかどうかのフラグです。
		 * trueの場合、Snackの編集画面に言語切り替えボタンが表示されます。
		 * しかし、言語切り替えによって要素のCSSを切り替えられるわけではないため不完全です。
		 * 現時点ではHPBにのみ許可している機能です。
		 * Snackではfalseを設定してください。
		 */
		isShowMultiLang: boolean;
		/**
		 * 他のページへのリンクを設定する際に指定できるリソースを設定します。
		 */
		pageResources: OtherResource[]; // 他のページへのリンク
		/**
		 * 予約コンポーネントを開く際に指定できるリソースを設定します。
		 */
		hotelPlanResources: OtherResource[];
		/**
		 * 予約コンポーネントを開く際に指定できるリソースを設定します。
		 */
		hotelRoomResources: OtherResource[];
		/**
		 * HPBであれば同一の施設のIDに紐づく施設の別のStaticPageのデータを流し込んでください。
		 * Snackであれば同一の施設のIDに紐づく施設の別のSnackのデータを流し込んでください。
		 *
		 * 以下のプロパティでカテゴリを作成してください
		 * uuid: "mypage"
		 * title: "マイテンプレート"
		 */
		templates: IChillnnTemplateCategoryModel;
		/**
		 * companyIDに紐づくテンプレートを流し込みます。
		 * SnackでもHPBでも同様のGraphQLのAPIを使ってください。
		 * Templateとして登録したCompanyに紐づくデータを取得してください。
		 *
		 * 以下のプロパティ名でカテゴリを作成してください
		 * uuid: "commonpage"
		 * title: "共通テンプレート"
		 */
		parentTemplates: IChillnnTemplateCategoryModel;
		/**
		 * SystemMasterで作成されたSnackテンプレートを流し込んでください。
		 */
		snackTemplates: IChillnnTemplateModelRepository;

		/**
		 * ## STUDIO内で「保存して終了」ボタンが押された際に呼び出される関数です。
		 * GraphQLのAPIを利用してデータを保存してください。
		 */
		callbackSaveAndExit: () => Promise<void>;
		/**
		 * ## STUDIO内で「一時保存」ボタンが押された際に呼び出される関数です。
		 * GraphQLのAPIを利用してデータを保存してください。
		 */
		callbackSave: () => Promise<void>;
	};
	shouldSync?: boolean;
};

const CHILLNN_STUDIO_DRIVER_ID = "chillnn-studio-driver";
const CHILLNN_STUDIO_DRIVER_EL_ID = "#" + CHILLNN_STUDIO_DRIVER_ID;

/**
 * @todo 初期描画時にフォーカスが当たっているアイテムのキーバインドが他のアイテムと共有されてしまっている
 * スナックやHPB（ホームページビルダー）で使用する、chillnn-studio-vue-driver のラッパー
 */
export const ChillnnStudioDriver = ({
	editorProps: {
		availableClickEvents = [
			StudioClickEventType.None,
			StudioClickEventType.ExternalLink,
			StudioClickEventType.StudioResource,
			StudioClickEventType.HotelReservation,
			StudioClickEventType.HotelReservationRoom,
		],
		...props
	},
	/**
	 * Editor での変更を React と同期するか
	 */
	shouldSync,
}: Props) => {
	const { showLoadingScreenCallback, hideLoadingScreenCallback } = useLoadingScreen();
	useEffect(() => {
		let isPrevShowLoading = false;
		const shouldShowLoading = !props.page;

		if (shouldShowLoading) {
			// pageが存在しない場合はローディングを表示
			showLoadingScreenCallback();
			isPrevShowLoading = true;
		} else {
			// pageが存在する場合はローディングを非表示
			if (isPrevShowLoading) {
				hideLoadingScreenCallback();
			}
		}

		return () => {
			if (isPrevShowLoading) {
				hideLoadingScreenCallback();
			}
		};
	}, [hideLoadingScreenCallback, props.page, showLoadingScreenCallback]);

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const chillnnStudioEditorRef = useRef<any>();

	/**
	 * unmount 時に ChillnnStudioEditor のインスタンスを破棄
	 */
	useEffect(() => {
		return () => {
			/**
			 * ChillnnStudioEditor のインスタンスを破棄
			 */
			if (chillnnStudioEditorRef.current) {
				chillnnStudioEditorRef.current.$destroy();
			}
		};
	}, []);

	/**
	 * 同期の可否
	 */
	const canSync = shouldSync;

	/**
	 * ChillnnStudioEditor を一度のみ初期化
	 */
	useEffect(() => {
		const canInitializeEditor = !chillnnStudioEditorRef.current && !!props.page && canSync;
		if (!canInitializeEditor) return;

		/**
		 * ChillnnStudioEditor のインスタンスを生成
		 */
		chillnnStudioEditorRef.current = new ChillnnStudioEditor({
			propsData: {
				availableClickEvents,
				...props,
			} as Props["editorProps"],
		});

		// マウント && DOM を描画
		chillnnStudioEditorRef.current.$mount(CHILLNN_STUDIO_DRIVER_EL_ID);
	}, [availableClickEvents, canSync, props]);

	/**
	 * ChillnnStudioEditor のインスタンスがマウントされているか
	 */
	const canUpdate = chillnnStudioEditorRef.current?._isMounted && canSync;
	/**
	 * ChillnnStudioEditor の依存を更新
	 */
	useEffect(() => {
		if (!canUpdate) return;

		/**
		 * ChillnnStudioEditor の依存を更新
		 */
		chillnnStudioEditorRef.current.callbackSave = props.callbackSave;
		chillnnStudioEditorRef.current.callbackSaveAndExit = props.callbackSaveAndExit;
	}, [availableClickEvents, canSync, canUpdate, chillnnStudioEditorRef, props]);

	return (
		<div>
			<div id={CHILLNN_STUDIO_DRIVER_ID} />
		</div>
	);
};

ChillnnStudioDriver.displayName = "ChillnnStudioDriver";
